import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Check, ClipboardCopy, Copy, FileText, RefreshCw } from "lucide-react";
import {
  useGetAgentsThread,
  useRetryAgentThread,
} from "../../../services/agents";
import { useGetAgentThreadInstructions } from "../../../services/admin";
import DropdownMenu from "../../common/DropdownMenu";
import EventsList from "./EventsList";

interface AgentDebugToolProps {
  threadRef: string;
  open: boolean;
  onClose: () => void;
}

interface TokenTotals {
  input: number;
  output: number;
  cacheRead: number;
  cacheCreation: number;
}

export const CopyIconButton = ({
  text,
  size = 14,
  tooltip = "Copy",
}: {
  text: string;
  size?: number;
  tooltip?: string;
}) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    },
    [text],
  );
  return (
    <Tooltip title={copied ? "Copied" : tooltip} placement="top">
      <IconButton size="small" onClick={handleCopy} sx={{ p: "3px" }}>
        {copied ? (
          <Check size={size} color="#15803D" />
        ) : (
          <Copy size={size} color="#9CA3AF" />
        )}
      </IconButton>
    </Tooltip>
  );
};

const formatTokens = (n: number): string => {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
};

const aggregateTokens = (executions: any[]): TokenTotals => {
  const totals: TokenTotals = {
    input: 0,
    output: 0,
    cacheRead: 0,
    cacheCreation: 0,
  };
  for (const exec of executions) {
    const meta = JSON.parse(exec.metadataJson || "{}");
    const usage = meta?.usage || {};
    totals.input += usage.input_tokens || 0;
    totals.output += usage.output_tokens || 0;
    totals.cacheRead += usage.cache_read_input_tokens || 0;
    totals.cacheCreation += usage.cache_creation_input_tokens || 0;
  }
  return totals;
};

type ThreadStatus = "active" | "completed" | "failed";

const STATUS_CONFIG: Record<
  ThreadStatus,
  { label: string; color: string; bg: string }
> = {
  active: { label: "Active", color: "#1D4ED8", bg: "#DBEAFE" },
  completed: { label: "Completed", color: "#15803D", bg: "#DCFCE7" },
  failed: { label: "Failed", color: "#B91C1C", bg: "#FEE2E2" },
};

const deriveThreadStatus = (events: string[]): ThreadStatus => {
  let status: ThreadStatus = "active";
  for (const eventStr of events) {
    const event = JSON.parse(eventStr);
    if (event.eventType === "thread_completed") status = "completed";
    if (event.eventType === "thread_failed") status = "failed";
  }
  return status;
};

export default ({ threadRef, open, onClose }: AgentDebugToolProps) => {
  const [threadData, setThreadData] = useState<any>(null);
  const [retrying, setRetrying] = useState(false);
  const [instructions, setInstructions] = useState<string | null>(null);
  const [instructionsLoading, setInstructionsLoading] = useState(false);
  const { error, request: getAgentsThread, loading } = useGetAgentsThread();
  const { request: retryAgentThread } = useRetryAgentThread();
  const { request: getInstructions } = useGetAgentThreadInstructions();

  useEffect(() => {
    if (open && threadRef) {
      setInstructions(null);
      fetchThreadData();
    }
  }, [open, threadRef]);

  const fetchThreadData = async () => {
    const response = await getAgentsThread({
      threadRef,
      includeAdminMessageHistory: true,
    });
    if (response) {
      setThreadData(response);
    }
  };

  const handleRetry = async () => {
    setRetrying(true);
    await retryAgentThread({ threadRef });
    await fetchThreadData();
    setRetrying(false);
  };

  const handleLoadInstructions = async () => {
    setInstructionsLoading(true);
    const resp = await getInstructions({ threadRef });
    if (resp) {
      setInstructions(resp.instructions);
    }
    setInstructionsLoading(false);
  };

  const handleCopyThreadJson = () => {
    if (threadData) {
      navigator.clipboard.writeText(JSON.stringify(threadData, null, 2));
    }
  };

  const executions = threadData?.executions || [];
  const totals = aggregateTokens(executions);
  const hasTokenData = totals.input > 0 || totals.output > 0;
  const cacheHitRate =
    totals.input > 0
      ? (
          (totals.cacheRead /
            (totals.input + totals.cacheRead + totals.cacheCreation)) *
          100
        ).toFixed(0)
      : null;
  const threadStatus = threadData?.events
    ? deriveThreadStatus(threadData.events)
    : null;
  const statusConfig = threadStatus ? STATUS_CONFIG[threadStatus] : null;

  const dropdownOptions = [
    {
      title: "View instructions",
      IconComponent: FileText,
      onClick: handleLoadInstructions,
      disabled: instructionsLoading,
    },
    {
      title: "Copy thread JSON",
      IconComponent: ClipboardCopy,
      onClick: handleCopyThreadJson,
      disabled: !threadData,
    },
    ...(threadStatus === "failed"
      ? [
          {
            title: retrying ? "Processing..." : "Process again",
            IconComponent: RefreshCw,
            onClick: handleRetry,
            disabled: retrying,
          },
        ]
      : []),
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        Agent Messages
        {statusConfig && (
          <Chip
            label={statusConfig.label}
            size="small"
            sx={{
              backgroundColor: statusConfig.bg,
              color: statusConfig.color,
              fontWeight: 600,
              fontSize: "12px",
            }}
          />
        )}
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {threadData && !loading && (
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 1,
                flexWrap: "wrap",
              }}
            >
              <Typography variant="caption">
                ({threadData.allMessages?.length || 0}) Messages
                {executions.length > 0 && (
                  <> • ({executions.length}) Executions</>
                )}
              </Typography>
              {hasTokenData && (
                <>
                  <Chip
                    label={`↓ ${formatTokens(totals.input)} in`}
                    size="small"
                    sx={{
                      backgroundColor: "#E3F2FD",
                      color: "#1565C0",
                      fontSize: "11px",
                    }}
                  />
                  <Chip
                    label={`↑ ${formatTokens(totals.output)} out`}
                    size="small"
                    sx={{
                      backgroundColor: "#E8F5E9",
                      color: "#2E7D32",
                      fontSize: "11px",
                    }}
                  />
                  {totals.cacheRead > 0 && (
                    <Chip
                      label={`⚡ ${formatTokens(totals.cacheRead)} cached${
                        cacheHitRate ? ` (${cacheHitRate}%)` : ""
                      }`}
                      size="small"
                      sx={{
                        backgroundColor: "#FFF3E0",
                        color: "#E65100",
                        fontSize: "11px",
                      }}
                    />
                  )}
                  {totals.cacheCreation > 0 && (
                    <Chip
                      label={`📦 ${formatTokens(totals.cacheCreation)} stored`}
                      size="small"
                      sx={{
                        backgroundColor: "#F3E5F5",
                        color: "#6A1B9A",
                        fontSize: "11px",
                      }}
                    />
                  )}
                </>
              )}
            </Box>
            {instructionsLoading && (
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <CircularProgress size={14} />
                <Typography variant="caption" color="text.secondary">
                  Loading instructions...
                </Typography>
              </Box>
            )}
            {instructions && (
              <Box
                sx={{
                  mb: 2,
                  p: 2,
                  backgroundColor: "#FFFBEB",
                  border: "1px solid #FDE68A",
                  borderRadius: "8px",
                  maxHeight: "300px",
                  overflowY: "auto",
                  position: "relative",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, color: "#92400E" }}
                  >
                    System Instructions
                  </Typography>
                  <CopyIconButton
                    text={instructions}
                    tooltip="Copy instructions"
                  />
                </Box>
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "#78350F",
                    whiteSpace: "pre-wrap",
                    fontFamily: "monospace",
                    lineHeight: 1.5,
                  }}
                >
                  {instructions}
                </Typography>
              </Box>
            )}
            <EventsList
              messages={threadData.allMessages || []}
              executions={executions}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <DropdownMenu
          buttonContent="Actions"
          options={dropdownOptions}
          variant="outlined"
          size="small"
          dropdownIcon
        />
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
